import type { Editor } from '@tiptap/core';

import { normalizeHighlightSearchText } from '../highlightSearchText';
import resolveQuoteSearchRoot from '../resolveQuoteSearchRoot';
import { createSearchTextIndex } from './searchTextIndex';
import type { TIndexedSearchText, TIndexedTextNodePosition, TTextMatch } from './types';

const findFallbackScrollRect = (node: HTMLElement | null): DOMRect | null => {
  for (let current: HTMLElement | null = node; current; current = current.parentElement) {
    const rect = current.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return rect;
  }

  return null;
};

const indexSearchTextNodes = (root: Node): TIndexedSearchText<TIndexedTextNodePosition> => {
  const index = createSearchTextIndex<TIndexedTextNodePosition>();
  const iter = document.createNodeIterator(root, NodeFilter.SHOW_TEXT);

  for (let node = iter.nextNode() as Text | null; node; node = iter.nextNode() as Text | null) {
    for (let offset = 0; offset < node.data.length; ) {
      const char = String.fromCodePoint(node.data.codePointAt(offset) ?? 0);
      const nextOffset = offset + char.length;
      index.add(char, { node, startOffset: offset, endOffset: nextOffset });
      offset = nextOffset;
    }
  }

  return index.done();
};

const findTextMatch = (editor: Editor, root: Node, highlightText: string): TTextMatch | null => {
  const needle = normalizeHighlightSearchText(highlightText);
  if (!needle) return null;

  const haystack = indexSearchTextNodes(root);
  const matchStart = haystack.text.indexOf(needle);
  if (matchStart === -1) return null;

  const matchEnd = matchStart + needle.length;
  const startPosition = haystack.positions[matchStart];
  const endPosition = haystack.positions[matchEnd - 1];

  if (!startPosition || !endPosition) return null;

  const from = editor.view.posAtDOM(startPosition.node, startPosition.startOffset);
  const to = editor.view.posAtDOM(endPosition.node, endPosition.endOffset);

  const domRange = document.createRange();
  domRange.setStart(startPosition.node, startPosition.startOffset);
  domRange.setEnd(endPosition.node, endPosition.endOffset);

  const rangeRect = domRange.getBoundingClientRect();
  const fallbackRect = findFallbackScrollRect(startPosition.node.parentElement);
  const scrollRect = rangeRect.height > 0 || rangeRect.width > 0 ? rangeRect : (fallbackRect ?? rangeRect);

  return {
    from,
    to,
    scrollRect,
  };
};

export const findQuoteMatch = (
  editor: Editor,
  element: HTMLElement | null,
  highlightText: string
): TTextMatch | null => {
  const root = resolveQuoteSearchRoot(element, editor.view.dom, highlightText);
  const scopedMatch = root ? findTextMatch(editor, root, highlightText) : null;

  if (scopedMatch || root === editor.view.dom) return scopedMatch;
  return findTextMatch(editor, editor.view.dom, highlightText);
};
